import { useState } from 'react';
import { Alert } from '@mui/material';

import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Small, Body } from '@/components/Typography';
import Badge from '@/components/Badge';
import {
  BackgroundCheckOverride,
  CheckrResponse,
  WorkersV2API,
} from '@/services/workersV2API';

import OverrideBackgroundCheckModal from './OverrideBackgroundCheckModal';

// Statuses graphy's BackgroundCheck::OverrideService accepts: no Checkr result
// yet. A consider/adverse result must be adjudicated in Checkr, not overridden.
const OVERRIDABLE_CHECKR_STATUSES = [null, undefined, '', 'report.created'];

type Props = {
  workerId: number;
  checkr: CheckrResponse;
  onSync?: () => void;
  // TENANT_ADMIN role only; graphy refuses helpdesk and client admins.
  canOverride?: boolean;
};

// graphy overrides are permanent: no Checkr result ends one, only an admin
// changing the background status. Permanent rows carry a 9999 sentinel in
// expires_at, so only rows from the 7-day era show an end date.
const OverrideNotice = ({
  override,
}: {
  override: BackgroundCheckOverride;
}) => (
  <Card.Section>
    <Alert
      severity={override.without_checkr_record ? 'error' : 'warning'}
      data-testid="background-check-override-notice"
    >
      {override.without_checkr_record &&
        'No Checkr check on file for this worker. '}
      Background check manually approved
      {override.created_by_name
        ? ` by ${override.created_by_name}`
        : ''} on {new Date(override.created_at).toLocaleDateString()}.{' '}
      {override.permanent
        ? 'Kept until an admin changes the background status.'
        : `Ends ${new Date(override.expires_at).toLocaleString()}.`}{' '}
      Reason: {override.reason}
    </Alert>
  </Card.Section>
);

const CheckrCard = ({
  workerId,
  checkr,
  onSync,
  canOverride = false,
}: Props) => {
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      const result = await WorkersV2API.syncFromCheckr(workerId);
      if (result.success) {
        setSyncResult({
          success: true,
          message: 'Sync completed successfully',
        });
        onSync?.();
      } else {
        setSyncResult({
          success: false,
          message: result.error || 'Sync failed',
        });
      }
    } catch (err) {
      setSyncResult({ success: false, message: 'Failed to sync from Checkr' });
    } finally {
      setSyncing(false);
    }
  };

  const override = checkr.override ?? null;
  // GRAVY-10976: a worker who never started a check (no Checkr candidate) can
  // be overridden too; the window warns that they have not been checked.
  const hasCandidate = 'candidate_id' in checkr && Boolean(checkr.candidate_id);
  const overrideAction =
    canOverride &&
    !override &&
    OVERRIDABLE_CHECKR_STATUSES.includes(checkr.checkr_status)
      ? [
          {
            label: 'Override',
            onAction: () => setShowOverrideModal(true),
            id: 'btn-override-background-check',
          },
        ]
      : [];

  const handleOverride = async (reason: string) => {
    await WorkersV2API.overrideBackgroundCheck(workerId, reason);
    onSync?.();
  };

  const overrideModal = showOverrideModal && (
    <OverrideBackgroundCheckModal
      withoutCheckrRecord={!hasCandidate}
      onClose={() => setShowOverrideModal(false)}
      onSubmit={handleOverride}
    />
  );

  if (!checkr.available) {
    // This is the branch admins see while Checkr is down, so the override
    // action and notice must render here too.
    return (
      <Card actions={overrideAction} title="Checkr (Background Check)">
        {overrideModal}
        {override && <OverrideNotice override={override} />}
        <Card.Section>
          <Stack vertical gap={8}>
            <Badge label="Not Available" status="neutral" />
            <Small color="lighter">
              {'reason' in checkr && checkr.reason
                ? checkr.reason
                : 'error' in checkr && checkr.error
                  ? checkr.error
                  : 'No Checkr data available for this worker'}
            </Small>
            {'candidate_id' in checkr && checkr.candidate_id && (
              <Small color="lighter">Candidate ID: {checkr.candidate_id}</Small>
            )}
            {'checkr_status' in checkr && checkr.checkr_status && (
              <Small color="lighter">Status: {checkr.checkr_status}</Small>
            )}
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card
      actions={[
        {
          label: syncing ? 'Syncing...' : 'Sync',
          onAction: handleSync,
          disabled: syncing,
        },
        ...overrideAction,
      ]}
      title="Checkr (Background Check)"
    >
      {overrideModal}
      {override && <OverrideNotice override={override} />}
      {syncResult && (
        <Card.Section>
          <Alert
            severity={syncResult.success ? 'success' : 'error'}
            onClose={() => setSyncResult(null)}
          >
            {syncResult.message}
          </Alert>
        </Card.Section>
      )}

      <Card.Section>
        <Stack vertical gap={16}>
          <Stack vertical gap={8}>
            <Small color="lighter">Status</Small>
            <Body>{checkr.checkr_status || 'N/A'}</Body>
          </Stack>

          <Stack vertical gap={8}>
            <Small color="lighter">Candidate ID</Small>
            <Body>{checkr.candidate_id}</Body>
          </Stack>

          {checkr.candidate.adjudication && (
            <Stack vertical gap={8}>
              <Small color="lighter">Adjudication</Small>
              <Body>{checkr.candidate.adjudication}</Body>
            </Stack>
          )}

          {checkr.reports && checkr.reports.length > 0 && (
            <Stack vertical gap={8}>
              <Small color="lighter">Reports ({checkr.reports.length})</Small>
              <Stack vertical gap={8}>
                {checkr.reports.map((report) => (
                  <div
                    key={report.id}
                    style={{
                      background: '#f5f5f5',
                      padding: '12px',
                      borderRadius: '4px',
                    }}
                  >
                    <Stack vertical gap={4}>
                      <Small>
                        <strong>ID:</strong> {report.id}
                      </Small>
                      <Small>
                        <strong>Status:</strong> {report.status}
                      </Small>
                      {report.result && (
                        <Small>
                          <strong>Result:</strong> {report.result}
                        </Small>
                      )}
                      {report.adjudication && (
                        <Small>
                          <strong>Adjudication:</strong> {report.adjudication}
                        </Small>
                      )}
                      {report.completed_at && (
                        <Small>
                          <strong>Completed:</strong>{' '}
                          {new Date(report.completed_at).toLocaleString()}
                        </Small>
                      )}
                    </Stack>
                  </div>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </Card.Section>

      <Card.Section>
        <Stack vertical gap={8}>
          <Small color="lighter">Full Data</Small>
          <pre
            style={{
              background: '#f5f5f5',
              padding: '16px',
              borderRadius: '4px',
              overflow: 'auto',
              fontSize: '12px',
              lineHeight: '1.5',
              margin: 0,
            }}
          >
            {JSON.stringify(checkr, null, 2)}
          </pre>
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default CheckrCard;
